import { useEffect, useRef } from 'react';
import styles from './Scope.module.css';

export default function Scope({ envelope0, envelope1 }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);

    const dataRef = useRef({
        lamella0: [],
        lamella1: []
    });

    const animationRef = useRef(null);
    const lastTimeRef = useRef(0);

    // Handle canvas resize
    useEffect(() => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const resizeCanvas = () => {
            const rect = container.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
        };

        resizeCanvas();
        const resizeObserver = new ResizeObserver(resizeCanvas);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const maxDataPoints = 200;
        const updateInterval = 16;

        const drawScope = (timestamp) => {
            if (timestamp - lastTimeRef.current < updateInterval) {
                animationRef.current = requestAnimationFrame(drawScope);
                return;
            }
            lastTimeRef.current = timestamp;

            const width = canvas.width;
            const height = canvas.height;

            dataRef.current.lamella0.push(envelope0);
            dataRef.current.lamella1.push(envelope1);

            if (dataRef.current.lamella0.length > maxDataPoints) {
                dataRef.current.lamella0.shift();
            }
            if (dataRef.current.lamella1.length > maxDataPoints) {
                dataRef.current.lamella1.shift();
            }

            ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
            ctx.fillRect(0, 0, width, height);

            drawGrid(ctx, width, height);

            const pointSpacing = width / maxDataPoints;

            drawWaveform(ctx, dataRef.current.lamella0, pointSpacing, width, height, '#00d4ff', 2);
            drawWaveform(ctx, dataRef.current.lamella1, pointSpacing, width, height, '#ff00ff', 2);

            animationRef.current = requestAnimationFrame(drawScope);
        };

        const drawGrid = (ctx, w, h) => {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
            ctx.lineWidth = 1;

            for (let i = 0; i <= 4; i++) {
                const y = (h / 4) * i;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(w, y);
                ctx.stroke();
            }

            for (let i = 0; i <= 10; i++) {
                const x = (w / 10) * i;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, h);
                ctx.stroke();
            }
        };

        const drawWaveform = (ctx, data, spacing, width, height, color, lineWidth) => {
            if (data.length < 2) return;

            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.shadowBlur = 8;
            ctx.shadowColor = color;

            ctx.beginPath();

            for (let i = 0; i < data.length; i++) {
                const x = width - (data.length - i) * spacing;
                const value = Math.max(0, Math.min(1, data[i]));
                const y = height - (value * height);

                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            }

            ctx.stroke();
            ctx.shadowBlur = 0;
        };

        animationRef.current = requestAnimationFrame(drawScope);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [envelope0, envelope1]);

    return (
        <div ref={containerRef} className={styles.container}>
            <canvas ref={canvasRef} className={styles.canvas} />
        </div>
    );
}
